import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Card, Col, DatePicker, Row, Select, Space, Spin, Table } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getLoyaltyReportsData } from "../../../../functions/reports";

dayjs.extend(weekOfYear);

const Container = styled.div`
  padding: 1rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
`;

const SummaryCard = styled(Card)`
  height: 100%;
`;

const TrendCard = styled(Card)`
  height: 100%;
  max-height: 800px;
`;

const MetricLabel = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.5rem;
`;

const MetricValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
`;

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#00ff00", "#0088fe"];

const LoyaltyReportsDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [period, setPeriod] = useState("last_30_days");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [granularity, setGranularity] = useState("day"); // day | week | month

  const getRangeForPeriod = (p) => {
    const today = dayjs();
    switch (p) {
      case "last_7_days":
        return { start: today.subtract(7, "day"), end: today.subtract(1, "day") };
      case "last_30_days":
        return { start: today.subtract(30, "day"), end: today.subtract(1, "day") };
      case "last_90_days":
        return { start: today.subtract(90, "day"), end: today.subtract(1, "day") };
      case "custom":
        return { start: startDate, end: endDate };
      default:
        return { start: today.subtract(30, "day"), end: today.subtract(1, "day") };
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { start, end } = getRangeForPeriod(period === "custom" ? "custom" : period);
        const useStart = start || dayjs().subtract(30, "day");
        const useEnd = end || dayjs().subtract(1, "day");

        const data = await getLoyaltyReportsData(useStart.toDate(), useEnd.toDate());
        setLoyaltyData(data);
      } catch (error) {
        console.error("Failed to load loyalty reports data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [period, startDate, endDate]);

  const aggregateTrendData = (data, mode) => {
    if (!data || mode === "day") return data || [];
    const groups = {};
    data.forEach((item) => {
      const d = dayjs(item.date, "DD-MM-YYYY");
      let key = item.date;
      let label = item.date;
      if (mode === "week") {
        const week = String(d.week()).padStart(2, "0");
        key = `${d.year()}-W${week}`;
        label = `${t("week")} ${week} ${d.year()}`;
      } else if (mode === "month") {
        key = d.format("YYYY-MM");
        label = d.format("MMM YYYY");
      }
      if (!groups[key]) {
        groups[key] = {
          key,
          date: label,
          earnedDelta: 0,
          cashedInDelta: 0,
          cashedInRewardsCountDelta: 0,
          pointsDelta: 0,
          usersActive: 0,
        };
      }
      groups[key].earnedDelta += item.earnedDelta || 0;
      groups[key].cashedInDelta += item.cashedInDelta || 0;
      groups[key].cashedInRewardsCountDelta += item.cashedInRewardsCountDelta || 0;
      groups[key].pointsDelta += item.pointsDelta || 0;
      groups[key].usersActive += item.usersActive || 0;
    });
    // sort by key chronologically
    const sortedKeys = Object.keys(groups).sort();
    return sortedKeys.map((k) => groups[k]);
  };

  const tableColumns = [
    { title: t("date"), dataIndex: "date", key: "date" },
    { title: t("loyalty_points_earned"), dataIndex: "earnedDelta", key: "earnedDelta" },
    { title: t("loyalty_points_cashed_in"), dataIndex: "cashedInDelta", key: "cashedInDelta" },
    { title: t("loyalty_rewards_count"), dataIndex: "cashedInRewardsCountDelta", key: "cashedInRewardsCountDelta" },
    { title: t("active_loyalty_users"), dataIndex: "usersActive", key: "usersActive" },
  ];

  return (
    <Container>
      <Title>{t("loyalty_reports")}</Title>
      {loading ? (
        <Spin />
      ) : (
        <>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <SectionTitle>{t("date_range")}</SectionTitle>
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  <Select
                    value={period}
                    onChange={(val) => setPeriod(val)}
                    options={[
                      { label: t("last_7_days"), value: "last_7_days" },
                      { label: t("last_30_days"), value: "last_30_days" },
                      { label: t("last_90_days"), value: "last_90_days" },
                      { label: t("custom_range"), value: "custom" },
                    ]}
                    style={{ width: "100%" }}
                  />
                  {period === "custom" && (
                    <DatePicker.RangePicker
                      value={[startDate, endDate]}
                      onChange={(vals) => {
                        setStartDate(vals?.[0] || null);
                        setEndDate(vals?.[1] || null);
                      }}
                      format="DD-MM-YYYY"
                      allowClear
                      style={{ width: "100%" }}
                    />
                  )}
                </Space>
              </Col>
              <Col xs={24} md={12}>
                <SectionTitle>{t("view_by")}</SectionTitle>
                <Select
                  value={granularity}
                  onChange={(val) => setGranularity(val)}
                  options={[
                    { label: t("per_day"), value: "day" },
                    { label: t("per_week"), value: "week" },
                    { label: t("per_month"), value: "month" },
                  ]}
                  style={{ width: "100%" }}
                />
              </Col>
            </Row>
          </Card>

          {loyaltyData && (
            <>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <SummaryCard>
                    <MetricLabel>{t("loyalty_points_earned")}</MetricLabel>
                    <MetricValue>{loyaltyData.totals.totalEarned}</MetricValue>
                  </SummaryCard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <SummaryCard>
                    <MetricLabel>{t("loyalty_points_cashed_in")}</MetricLabel>
                    <MetricValue>{loyaltyData.totals.totalCashedIn}</MetricValue>
                  </SummaryCard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <SummaryCard>
                    <MetricLabel>{t("loyalty_rewards_count")}</MetricLabel>
                    <MetricValue>{loyaltyData.totals.totalRewards}</MetricValue>
                  </SummaryCard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <SummaryCard>
                    <MetricLabel>{t("distributed_points")}</MetricLabel>
                    <MetricValue>{loyaltyData.totals.totalPointsChange}</MetricValue>
                  </SummaryCard>
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24}>
                  <TrendCard title={<SectionTitle style={{ margin: 0 }}>{t("loyalty_trends")}</SectionTitle>}>
                    <div style={{ width: "100%", height: 340 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={aggregateTrendData(loyaltyData.trendData, granularity)}
                          margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis allowDecimals={false} />
                          <RechartsTooltip />
                          <Line
                            type="monotone"
                            dataKey="earnedDelta"
                            stroke={COLORS[0]}
                            name={t("loyalty_points_earned")}
                          />
                          <Line
                            type="monotone"
                            dataKey="cashedInDelta"
                            stroke={COLORS[1]}
                            name={t("loyalty_points_cashed_in")}
                          />
                          <Line
                            type="monotone"
                            dataKey="usersActive"
                            stroke={COLORS[2]}
                            name={t("active_loyalty_users")}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </TrendCard>
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24}>
                  <Card>
                    <SectionTitle>{t("loyalty_details")}</SectionTitle>
                    <Table
                      size="small"
                      dataSource={aggregateTrendData(loyaltyData.trendData, granularity)}
                      columns={tableColumns}
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: 800 }}
                      rowKey="date"
                    />
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default LoyaltyReportsDashboard;
